import { isPlatformBrowser, isPlatformServer, PlatformLocation } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Request } from 'express';
import { CookieService } from 'ngx-cookie-service';
import { REQUEST } from '../../express.tokens';
import { getEnv } from '../lib/get-env';
import { getOrigin } from '../lib/get-origin';
import { Environment } from '../providers/config.service';
import { StorageService } from './storage.service';
import { TransferStateService } from './transfer-state.service';

@Injectable({
  providedIn: 'root',
})
export class BrowserService {
  private platformId = inject<string>(PLATFORM_ID);
  private req = inject<Request>(REQUEST, { optional: true });
  private location = inject(PlatformLocation);
  private cookieService = inject(CookieService);
  private transferState = inject(TransferStateService);
  private storage = inject(StorageService);
  fakeTenant: string;

  constructor() {
    if (this.getEnv() === 'local') {
      const searchParams = new URLSearchParams(this.location.search);
      const fakeTenant = searchParams.get('fake-tenant') || this.storage.get<string>('fake-tenant');
      this.fakeTenant = fakeTenant;
      this.storage.set('fake-tenant', fakeTenant, true);
    }
  }

  /**
   * @returns https://picflow.com | https://dev.picflow.com | https://custom.picflow.com | http://localhost:4200
   */
  public getOrigin(): string {
    return this.transferState.withStateTransferInstant('PROTECTED_origin', (): string => {
      // If we get to this point (inside state transfer) and we are not running on the server
      // it means we are running without SSR. We are assuming that this only happens when running locally
      if (isPlatformBrowser(this.platformId)) return window.location.origin;
      return getOrigin(this.req);
    });
  }

  public getEnv(): Environment {
    return getEnv(this.getOrigin());
  }

  public isExtension(): boolean {
    if (this.getCookies()?.fakeExtensionMode === 'true') return true;
    const origin = this.getOrigin();
    if (origin.startsWith('chrome-extension://')) return true;
    return false;
  }

  /**
   * Either sub domain or custom domain
   */
  public isTenantDomain(): boolean {
    return !!this.getTenantCustomDomain() || !!this.getTenantSubDomain();
  }

  public getTenantCustomDomain(): string {
    return this.transferState.withStateTransferInstant('PROTECTED_tenant-custom-domain', (): string => {
      if (isPlatformBrowser(this.platformId)) return;
      const host = <string>this.req?.headers['custom-domain-host'];
      if (!host) return undefined;
      return host.split('www.').join('');
    });
  }

  public getTenantSubDomain(): string {
    return this.transferState.withStateTransferInstant('PROTECTED_tenant-sub-domain', (): string => {
      // If we get to this point (inside state transfer) and we are not running on the server
      // it means we are running without SSR. We are assuming that this only happens when running locally
      const tenant = this.fakeTenant;
      if (tenant) return tenant;
      if (isPlatformBrowser(this.platformId)) return;
      const origin = this.getOrigin();
      if (this.getEnv() === 'local') return;
      if (!origin.endsWith('picflow.com')) return; // We only want picflow subdomains
      const subDomain = origin
        .split('://')[1]
        .split('.')
        .filter(a => a !== 'www')[0];
      if (!subDomain) return;
      if (['dev', 'test', 'picflow'].includes(subDomain)) return;
      return subDomain;
    });
  }

  public getCookies(): { [key: string]: string } {
    if (isPlatformBrowser(this.platformId)) return this.cookieService.getAll();
    const raw = this.req?.get('Cookie') || '';
    const array = raw.split('; ');
    const obj: { [key: string]: string } = {};
    array.forEach(v => {
      const vals = v.split('=');
      obj[vals[0]] = vals[1];
    });
    return obj;
  }

  public urlIsAssetUrl(url: string) {
    return url.includes('/img_') || url.includes('/asset_');
  }

  public urlIsGalleryUrl(url: string) {
    const isAssetUrl = this.urlIsAssetUrl(url);
    if (isAssetUrl) return false;
    if (this.getTenantSubDomain()) return true;
    if (this.getTenantCustomDomain()) return true;
    if (url.includes('/gallery/')) return true;
    return false;
  }

  /**
   * @returns https://picflow.com | https://dev.picflow.com | http://localhost:4200
   */
  public getCorrectPicflowUrl() {
    const env = this.getEnv();
    if (env === 'local') return this.getOrigin();
    if (env === 'dev') return 'https://dev.picflow.com';
    if (env === 'test') return 'https://test.picflow.com';
    if (env === 'production') return 'https://picflow.com';
  }

  public reload(force = false) {
    if (isPlatformServer(this.platformId)) {
      console.warn('Cannot reload on server');
      return;
    }
    if (window.onbeforeunload && !force) return;

    window.onbeforeunload = null;
    location.reload();
  }
}
